/**
 * Hotel Groups List Page
 * 
 * Shows all hotel groups with quick stats.
 * Click a group → Group Management Page
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hotelGroupsAPI, hotelsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Building2,
  ArrowLeft,
  Loader2,
  Plus,
  Users,
  BarChart3,
  ChevronRight,
  Layers,
  Hotel,
} from 'lucide-react';

export default function HotelGroupsList() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [groupsRes, hotelsRes] = await Promise.all([
        hotelGroupsAPI.list(),
        hotelsAPI.list(),
      ]);
      setGroups(groupsRes.data || []);
      setHotels(hotelsRes.data || []);
    } catch (error) {
      console.error('Failed to load groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const createGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    setSaving(true);
    try {
      await hotelGroupsAPI.create(newGroup);
      toast.success('Group created');
      setDialogOpen(false);
      setNewGroup({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create group');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(224,71%,4%)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(224,71%,4%)]">
      {/* Header */}
      <header className="border-b border-[hsl(217,19%,27%)] bg-[hsl(217,19%,12%)] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/hotels')}
                className="flex items-center space-x-2 text-[hsl(215,20%,65%)] hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Dashboard</span>
              </button>
              <div className="h-6 w-px bg-[hsl(217,19%,27%)]" />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white">Hotel Groups</h1>
                  <p className="text-xs text-[hsl(215,20%,65%)]">Multi-property management</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Group
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {groups.length === 0 ? (
          <Card className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-4">
                <Layers className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No Groups Yet</h3>
              <p className="text-[hsl(215,20%,65%)] text-center max-w-md mb-6">
                Create a group to manage multiple hotels together. Perfect for franchise operators 
                or regional managers overseeing several properties.
              </p>
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Group
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((group) => (
              <Card
                key={group.id}
                className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] cursor-pointer hover:border-purple-500/50 transition-colors group"
                onClick={() => navigate(`/hotels/groups/${group.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Layers className="w-6 h-6 text-purple-500" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-[hsl(215,20%,45%)] group-hover:text-white transition-colors" />
                  </div>
                  <CardTitle className="text-white mt-3">{group.name}</CardTitle>
                  {group.description && (
                    <CardDescription className="text-[hsl(215,20%,65%)]">
                      {group.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-6 text-sm">
                    <div className="flex items-center text-[hsl(215,20%,65%)]">
                      <Hotel className="w-4 h-4 mr-1.5 text-blue-400" />
                      <span>{group.hotel_count || 0} hotels</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Ungrouped Hotels Section */}
        {hotels.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-medium text-white mb-4">All Hotels</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {hotels.map((hotel) => (
                <Card
                  key={hotel.id}
                  className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] cursor-pointer hover:border-blue-500/50 transition-colors"
                  onClick={() => navigate(`/hotels/${hotel.id}/manage`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{hotel.name}</p>
                        <p className="text-xs text-[hsl(215,20%,65%)] truncate">
                          {hotel.city || hotel.hotel_code || 'No location'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Create Group Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
          <DialogHeader>
            <DialogTitle className="text-white">Create Hotel Group</DialogTitle>
            <DialogDescription className="text-[hsl(215,20%,65%)]">
              Create a group to manage multiple hotels collectively.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white">Group Name *</Label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="Downtown Properties"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Input
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                placeholder="All downtown hotel locations"
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={createGroup} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
